import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import QuoteModal from '@/components/QuoteModal';
import LazyImage from '@/components/LazyImage';
import SEO from '@/components/SEO';
import { seoData } from '@/utils/seoData';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Wrench, Shield, Clock, Award, ArrowRight, CheckCircle2, Package, Sparkles, Quote, ChevronDown, ChevronUp } from 'lucide-react';

export const HomePage = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  useEffect(() => {
    // Scroll-triggered animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });

    // Parallax effect for hero section
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const parallaxElements = document.querySelectorAll('.parallax');
      parallaxElements.forEach((el) => {
        const speed = el.dataset.speed || 0.5;
        el.style.transform = `translateY(${scrolled * speed}px)`;
      });
    };

    // Listen for WhatsApp widget modal trigger
    const handleOpenModalEvent = () => {
      setQuoteModalOpen(true);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('openQuoteModal', handleOpenModalEvent);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('openQuoteModal', handleOpenModalEvent);
      observer.disconnect();
    };
  }, []);

  const handleBrandServiceClick = (brandName, serviceType) => {
    setSelectedBrand(brandName);
    setSelectedService(serviceType);
    setQuoteModalOpen(true);
  };

  const features = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Expert Mechanics',
      description: 'Certified professionals with years of experience in automotive repair and maintenance.',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Quality Parts',
      description: 'Genuine auto parts from trusted brands ensuring reliability and longevity.',
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Fast Service',
      description: 'Quick turnaround times without compromising on quality workmanship.',
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Warranty Guaranteed',
      description: 'All our services and parts come with comprehensive warranty coverage.',
    },
  ];

  const services = [
    {
      title: 'Engine Repair',
      description: 'Complete engine diagnostics and repair services',
      image: '/images/workshop.jpg',
    },
    {
      title: 'Brake Service',
      description: 'Professional brake inspection and replacement',
      image: '/images/tire-service.jpg',
    },
    {
      title: 'Oil & Maintenance',
      description: 'Regular maintenance and oil change services',
      image: '/images/car-service.jpg',
    },
  ];

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '5000+', label: 'Happy Clients' },
    { number: '50+', label: 'Expert Mechanics' },
    { number: '24/7', label: 'Support Available' },
  ];

  // All brands for scrolling section
  const allBrands = [
    { name: 'Toyota', logo: '/images/brands/toyota.png' },
    { name: 'Honda', logo: '/images/brands/honda.png' },
    { name: 'Suzuki', logo: '/images/brands/suzuki.png' },
    { name: 'Hyundai', logo: '/images/brands/hyundai.png' },
    { name: 'KIA', logo: '/images/brands/kia.png' },
    { name: 'Changan', logo: '/images/brands/changan.png' },
    { name: 'MG', logo: '/images/brands/mg.png' },
    { name: 'BYD', logo: '/images/brands/byd.png' },
    { name: 'BMW', logo: '/images/brands/bmw.png' },
    { name: 'Audi', logo: '/images/brands/audi.png' },
    { name: 'Mercedes', logo: '/images/brands/mercedes.png' },
    { name: 'Ford', logo: '/images/brands/ford.png' },
    { name: 'Chevrolet', logo: '/images/brands/chevrolet.png' },
    { name: 'Nissan', logo: '/images/brands/nissan.png' },
    { name: 'Mitsubishi', logo: '/images/brands/mitsubishi.png' },
    { name: 'Mazda', logo: '/images/brands/mazda.png' },
    { name: 'Daihatsu', logo: '/images/brands/daihatsu.png' },
    { name: 'FAW', logo: '/images/brands/faw.png' },
    { name: 'DFSK', logo: '/images/brands/dfsk.png' },
    { name: 'Chery', logo: '/images/brands/chery.png' },
    { name: 'Geely', logo: '/images/brands/geely.png' },
    { name: 'Haval', logo: '/images/brands/haval.png' },
    { name: 'Proton', logo: '/images/brands/proton.png' },
    { name: 'Prince', logo: '/images/brands/prince.png' },
    { name: 'United', logo: '/images/brands/united.png' },
    { name: 'Isuzu', logo: '/images/brands/isuzu.png' },
    { name: 'Volkswagen', logo: '/images/brands/volkswagen.png' },
    { name: 'Lexus', logo: '/images/brands/lexus.png' },
    { name: 'Subaru', logo: '/images/brands/subaru.png' },
  ];

  // Popular brands for cards section
  const popularBrands = allBrands.slice(0, 8);

  // FAQs data
  const faqs = [
    {
      question: 'Do you use genuine auto parts?',
      answer: 'Yes, we use only genuine and certified auto parts from trusted brands. All our parts come with manufacturer warranty and quality guarantee.'
    },
    {
      question: 'What brands do you service?',
      answer: 'We service all major brands including Toyota, Honda, Suzuki, Hyundai, KIA, and many more. Our mechanics are trained to work on both local and imported vehicles.'
    },
    {
      question: 'How long does a typical repair take?',
      answer: 'Repair time depends on the service type. Simple maintenance like oil changes take 30-60 minutes, while major repairs may take 1-3 days. We always provide estimated timeframes upfront.'
    },
    {
      question: 'Do you offer warranty on repairs?',
      answer: 'Yes, all our repairs and parts come with comprehensive warranty coverage. The warranty period varies by service type and parts used.'
    },
    {
      question: 'Can I order specific auto parts?',
      answer: 'Absolutely! We can order any genuine auto part for your vehicle. Just contact us with your vehicle details and the part you need, and we will arrange it for you.'
    },
    {
      question: 'Do you provide pickup and delivery service?',
      answer: 'Yes, we offer free pickup and delivery service within the city limits for major repairs and servicing. Contact us to schedule this service.'
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: 'Ahmed Hassan',
      role: 'Toyota Owner',
      text: 'Best auto service in Pakistan! The team is professional and honest. They fixed my engine issue in no time.',
      rating: 5,
    },
    {
      name: 'Fatima Khan',
      role: 'Honda Customer',
      text: 'Excellent service and genuine parts. I trust them completely with my car maintenance. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Imran Ali',
      role: 'Suzuki Owner',
      text: 'Very professional staff and fair pricing. They explained everything clearly and the repair quality is outstanding.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-background noise-bg">
      <SEO 
        title={seoData.home.title}
        description={seoData.home.description}
        keywords={seoData.home.keywords}
        ogImage={seoData.home.ogImage}
        structuredData={seoData.home.structuredData}
      />
      <Navbar />

      {/* Hero Section - Enhanced with 3D & Animations */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden particles-bg geometric-bg">
        {/* Animated Background */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ y }}
        >
          <LazyImage
            src="/images/banner1.jpg"
            alt="Bilal Ganj Auto Parts - Professional Auto Service"
            className="w-full h-full object-cover"
            fadeInDuration={0.8}
            threshold={0}
            isBanner={true}
            dualRingLoader={true}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50"></div>
          {/* Floating circles for depth */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-primary/10 rounded-full blur-3xl float-animation"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-accent/10 rounded-full blur-3xl float-animation" style={{animationDelay: '1s'}}></div>
        </motion.div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-20">
          <div className="max-w-3xl">
            {/* Animated Heading with Type Effect */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="block"
                >
                  Your Trusted
                </motion.span>
                <span className="gradient-text block mt-2 text-glow">
                  <TypeAnimation
                    sequence={[
                      'Auto Parts & Repair',
                      2000,
                      'Professional Service',
                      2000,
                      'Quality Solutions',
                      2000,
                    ]}
                    wrapper="span"
                    speed={50}
                    repeat={Infinity}
                  />
                </span>
                <motion.span
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="block"
                >
                  Specialists
                </motion.span>
              </h1>
            </motion.div>

            {/* Animated Description */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
                Professional car repair services and genuine auto parts for all major brands in Pakistan. 
                Quality you can trust, service you can count on.
              </p>
            </motion.div>

            {/* Animated Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <Link to="/services">
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button size="lg" className="btn-glow-blue btn-3d bg-primary hover:bg-primary/90 text-primary-foreground text-base px-8 py-6">
                    Our Services
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </Link>
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  onClick={() => setQuoteModalOpen(true)}
                  size="lg" 
                  variant="outline" 
                  className="text-base px-8 py-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground border-glow shimmer"
                  data-testid="hero-get-started-btn"
                >
                  <Sparkles className="mr-2 w-5 h-5" />
                  Get Started
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
        >
          <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              className="w-1 h-2 bg-primary rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section - Enhanced with 3D Glass Effect */}
      <section className="py-16 bg-card border-y border-border relative overflow-hidden">
        <div className="absolute inset-0 geometric-bg opacity-30"></div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <motion.div
                  className="glass-card neomorphic-card p-6 rounded-2xl"
                  whileHover={{ 
                    scale: 1.05,
                    rotateY: 5,
                    rotateX: 5,
                  }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <motion.div 
                    className="text-4xl lg:text-5xl font-bold text-primary mb-2 text-glow"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: index * 0.1 + 0.3,
                      type: 'spring',
                      stiffness: 200
                    }}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Message Section - NEW */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                className="relative"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <LazyImage
                    src="/images/bilalceo.jpg"
                    alt="Muhammad Bilal - CEO Bilal Ganj Auto Parts"
                    className="w-full h-auto object-cover"
                    dualRingLoader={true}
                    fadeInDuration={0.8}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-2xl font-bold text-foreground mb-1">Muhammad Bilal</h3>
                  <p className="text-primary font-medium">Founder & CEO</p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="mb-6">
                  <Quote className="w-12 h-12 text-primary/30" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">Message from Our CEO</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p className="text-lg">
                    At Bilal Ganj Auto Parts, quality isn't just a promise—it's our commitment to you. 
                    With over a decade of experience, we've built our reputation on trust, genuine parts, 
                    and exceptional service that keeps your vehicle running at its best.
                  </p>
                  <p className="text-foreground font-medium italic text-xl mt-6">
                    "Your vehicle's performance is our priority. We treat every car as if it were our own."
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-foreground font-semibold">Muhammad Bilal</p>
                  <p className="text-sm text-muted-foreground">Founder & CEO</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - NEW */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                About <span className="gradient-text">Bilal Ganj Auto Parts</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Founded in 2014, Bilal Ganj Auto Parts has been Pakistan's trusted partner for automotive excellence. 
                We specialize in providing genuine auto parts and professional repair services for all major brands. 
                With over 50 certified mechanics, state-of-the-art equipment, and a commitment to quality, 
                we've served more than 5,000 satisfied customers.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Genuine Parts</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                    <Shield className="w-10 h-10 text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Warranty</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                    <Wrench className="w-10 h-10 text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Expert Service</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                    <Award className="w-10 h-10 text-primary" />
                  </div>
                  <p className="font-semibold text-foreground">Certified</p>
                </div>
              </div>
              <div className="mt-10">
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                    Learn More About Us
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced with 3D Cards */}
      <section className="py-20 lg:py-32 relative particles-bg">
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          {/* Animated Section Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="gradient-text">Why Choose Us</span>
            </h2>
            <motion.p
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              We combine expertise, quality, and customer service to deliver the best automotive solutions.
            </motion.p>
          </motion.div>

          {/* Animated Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <motion.div
                  whileHover={{ 
                    y: -12,
                    rotateX: 5,
                    rotateY: -5,
                    scale: 1.02
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <Card className="glass-card border-border hover:border-primary/50 transition-all duration-300 h-full group scan-line">
                    <CardContent className="p-6 flex flex-col h-full">
                      <motion.div
                        className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4 glow-pulse"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        {feature.icon}
                      </motion.div>
                      <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {feature.description}
                      </p>
                      {/* 3D Depth Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none"></div>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Our Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive automotive solutions for all your vehicle needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-3d overflow-hidden bg-background border-border hover:border-primary/50 transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <LazyImage
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 service-image"
                    fadeInDuration={0.7}
                    pulseLoader={true}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-muted-foreground mb-4">{service.description}</p>
                  <Link to="/services" className="text-primary hover:text-primary/80 font-medium inline-flex items-center">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Autoparts Variety Section with Scrolling Brands - NEW */}
      <section className="py-20 lg:py-32 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Auto Parts <span className="gradient-text">Variety</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Genuine parts available for all major automotive brands
            </p>
          </div>

          {/* Scrolling Brands Carousel */}
          <div className="relative mb-12">
            <div className="overflow-hidden">
              <motion.div 
                className="flex gap-8 py-8"
                animate={{ 
                  x: [0, -2000]
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 30,
                    ease: "linear"
                  }
                }}
              >
                {/* Duplicate brands for seamless loop */}
                {[...allBrands, ...allBrands].map((brand, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 w-32 h-32 bg-card rounded-lg border border-border hover:border-primary/50 transition-all duration-300 flex items-center justify-center p-4 glass-card"
                  >
                    <LazyImage
                      src={brand.logo}
                      alt={brand.name}
                      className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                      wrapperClassName="w-full h-full flex items-center justify-center"
                      fadeInDuration={0.3}
                      spinnerLoader={true}
                    />
                  </div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Order Part Button */}
          <div className="text-center">
            <Button 
              onClick={() => {
                setSelectedService('order-part');
                setQuoteModalOpen(true);
              }}
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-10 py-7"
              data-testid="order-part-btn"
            >
              <Package className="mr-2 w-6 h-6" />
              Order Part
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section - NEW */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <Card className="card-3d bg-background border-border hover:border-primary/50 transition-all duration-300 h-full">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg
                          key={i}
                          className="w-5 h-5 text-accent fill-current"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                    <Quote className="w-8 h-8 text-primary/30 mb-4" />
                    <p className="text-muted-foreground mb-6 leading-relaxed italic">
                      "{testimonial.text}"
                    </p>
                    <div className="pt-4 border-t border-border">
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section - NEW */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Frequently Asked <span className="gradient-text">Questions</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Find answers to common questions about our services and parts
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <Card className="card-3d bg-card border-border hover:border-primary/50 transition-all duration-300">
                    <CardContent className="p-0">
                      <button
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        className="w-full text-left p-6 flex items-center justify-between"
                        data-testid={`faq-question-${index}`}
                      >
                        <h3 className="text-lg font-semibold pr-4">{faq.question}</h3>
                        {openFaqIndex === index ? (
                          <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      {openFaqIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-6 pb-6"
                        >
                          <p className="text-muted-foreground leading-relaxed">
                            {faq.answer}
                          </p>
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Trusted Brands We Service
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Quality parts and expert service for all major automotive brands
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularBrands.map((brand, index) => (
              <Card key={index} className="card-3d bg-background border-border hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6 flex flex-col items-center">
                  <div className="w-full h-24 flex items-center justify-center mb-4">
                    <LazyImage
                      src={brand.logo}
                      alt={brand.name}
                      className="max-w-full max-h-full object-contain brand-image"
                      wrapperClassName="w-full h-full flex items-center justify-center"
                      fadeInDuration={0.5}
                      spinnerLoader={true}
                    />
                  </div>
                  <h3 className="text-xl font-semibold mb-4 text-center">{brand.name}</h3>
                  <div className="flex flex-col gap-2 w-full">
                    <Button
                      onClick={() => handleBrandServiceClick(brand.name, 'servicing')}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center gap-2"
                      size="sm"
                    >
                      <Wrench size={16} />
                      Servicing
                    </Button>
                    <Button
                      onClick={() => handleBrandServiceClick(brand.name, 'order-part')}
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground flex items-center justify-center gap-2"
                      size="sm"
                    >
                      <Package size={16} />
                      Order Part
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/brands">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                View All Brands
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/5"></div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us today for professional automotive services and quality auto parts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Contact Us Now
                </Button>
              </Link>
              <Button 
                onClick={() => setQuoteModalOpen(true)}
                size="lg" 
                variant="outline" 
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Modal */}
      <QuoteModal 
        open={quoteModalOpen} 
        onOpenChange={setQuoteModalOpen}
        preSelectedBrand={selectedBrand}
        preSelectedService={selectedService}
      />

      <Footer />
    </div>
  );
};

export default HomePage;